import * as React from 'react';
import {forwardRef, PropsWithChildren, ReactNode, useCallback, useContext, useMemo} from 'react';
import {Glyph} from '../../types/Glyph';
import {isNil, some} from 'lodash-es';
import Helpers from '../../../shared-common/helpers/Helpers';
import {StackContainer} from '../../../shared-ui/components/StackContainer/StackContainer';
import {Icon} from '../Icon/Icon';
import {SurfaceProminence} from '../../types/SurfaceProminence';
import {ThemeProvider} from '../../context/ThemeProvider';
import RenderHelpers from '../../../shared-ui/helpers/RenderHelpers';
import styles from './Button.module.css';
import {useSyncState} from '../../../shared-common/hooks/useSyncState';
import {SharedComponentProps} from '../../types/SharedComponentProps';
import {ButtonBehaviour} from '../../types/ButtonBehaviour';
import {SurfaceState} from '../../types/SurfaceState';
import {SelectionManager} from '../../context/SelectionManager';
import {InteractionEffect} from '../../types/InteractionEffect';
import {SurfaceDensity} from '../../types/SurfaceDensity';
import {GlyphStyle} from '../../types/GlyphStyle';
import {Position} from '../../../shared-ui/types/Position';

export interface ButtonProps extends SharedComponentProps {
  icon?: Glyph | ReactNode;
  iconStyle?: GlyphStyle;
  iconPosition?: Position;
  text?: string;
  behaviour?: ButtonBehaviour;
  effect?: InteractionEffect;
  value?: unknown;
  isDisabled?: boolean;
  isLoading?: boolean;
  tabIndex?: number;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => Promise<void> | void;
  onMouseOver?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onMouseOut?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onMouseEnter?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onMouseLeave?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onMouseDown?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onMouseUp?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLButtonElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLButtonElement>) => void;
}

type ButtonContent = 'children' | 'iconAndText' | 'icon' | 'text';

export const Button = forwardRef<HTMLButtonElement, PropsWithChildren<ButtonProps>>(
  (props, ref) => {
    const {getSurfaceStyle} = useContext(ThemeProvider);
    const {
      hasSelectionManager,
      selectedValues,
      onSelected,
      onDeselected,
      equalityComparer
    } = useContext(SelectionManager);
    const [getIsSelected, setIsSelected] = useSyncState(false);
    const [getIsPressed, setIsPressed] = useSyncState(false);

    const isLoading = props.isLoading === true;
    const isVisuallyDisabled = props.isDisabled === true;
    const isDisabled = isVisuallyDisabled || isLoading || getIsPressed();

    const prominence: SurfaceProminence = Helpers.ifNil(props.prominence, 'elevated');
    const density: SurfaceDensity = Helpers.ifNil(props.density, 'default');
    const behaviour: ButtonBehaviour = Helpers.ifNil(props.behaviour, 'normal');

    const iconPosition: Position = Helpers.ifNil(props.iconPosition, 'left');
    const isHorizontal = iconPosition === 'left' || iconPosition === 'right';

    let isCurrentlySelected = getIsSelected();
    if (hasSelectionManager) {
      isCurrentlySelected = some(selectedValues, v => equalityComparer(v, props.value));
    }

    let state: SurfaceState = 'normal';

    if (isVisuallyDisabled) {
      state = 'disabled';
    } else if (getIsPressed()) {
      state = 'pressed';
    } else if (behaviour !== 'normal' && isCurrentlySelected) {
      state = 'selected';
    }

    const handleClick = useCallback(
      async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (isDisabled) {
          return;
        }

        try {
          setIsPressed(true);

          if (behaviour === 'toggle' || behaviour === 'radio') {
            if (behaviour === 'toggle') {
              if (hasSelectionManager) {
                if (isCurrentlySelected) {
                  onDeselected(props.value);
                } else {
                  onSelected(props.value);
                }
              } else {
                setIsSelected(isCurrentlySelected);
              }
            } else if (behaviour === 'radio') {
              if (hasSelectionManager) {
                onSelected(props.value);
              } else {
                setIsSelected(true);
              }
            }
          }

          if (isNil(props.onClick)) {
            return;
          }

          const result = props.onClick(event);
          if (result !== null && typeof result === 'object') {
            await result;
            return;
          }
        } finally {
          setIsPressed(false);
        }
      },
      [props.onClick, getIsPressed, setIsPressed, isDisabled, props.value]
    );

    const {defaultTheme, hoverTheme} = useMemo(
      () => {

        const effect = Helpers.ifNil(props.effect, 'none');

        return {
          defaultTheme: getSurfaceStyle(
            props.theme,
            'button',
            effect,
            prominence,
            density,
            state
          ),
          hoverTheme: getSurfaceStyle(
            props.theme,
            'button',
            effect,
            prominence,
            density,
            'hovered'
          )
        };
      },
      [getSurfaceStyle, state, props.prominence, props.effect]
    );

    const contentType: ButtonContent = useMemo(
      () => {
        const hasChildren = React.Children.count(props.children) > 0;
        const hasText = !isNil(props.text);
        const hasIcon = !isNil(props.icon);

        if (hasChildren) {
          return 'children';
        }

        if (hasText && hasIcon) {
          return 'iconAndText';
        }

        if (hasIcon) {
          return 'icon';
        }

        return 'text';
      },
      [props.icon, props.text, props.children]
    );

    const content = useMemo(
      () => {
        switch (contentType) {
          case 'children':
            return props.children;
          case 'iconAndText':
            return (
              <StackContainer
                stackFrom={iconPosition}
                gapWidth={defaultTheme.childSpacing}
                horizontalAlignment={isHorizontal ? undefined : 'center'}
                verticalAlignment={isHorizontal ? 'center' : undefined}
              >
                <Icon glyph={isLoading ? 'busy' : props.icon} glyphStyle={props.iconStyle}/>
                <span className={styles.text}>{props.text}</span>
              </StackContainer>
            );
          case 'icon':
            return (<Icon glyph={isLoading ? 'busy' : props.icon} glyphStyle={props.iconStyle}/>);
          default:
          case 'text':
            return (<span className={styles.text}>{props.text}</span>);
        }
      },
      [contentType, isLoading, props.icon, props.text, props.children]
    );

    const withCustomContent = contentType === 'children';

    return (
      <button
        ref={ref}
        tabIndex={props.tabIndex}
        className={RenderHelpers.classes(
          styles.component,
          props.className,
          contentType === 'text' || contentType === 'iconAndText' ? styles.hasText : null,
          contentType === 'icon' || contentType === 'iconAndText' ? styles.hasIcon : null
        )}
        style={{
          ...{
            '--background-color': defaultTheme.backgroundColor,
            '--border-color': defaultTheme.borderColor,
            '--border-width': defaultTheme.borderWidth,
            '--text-color': defaultTheme.textColor,
            '--highlight-color': defaultTheme.highlightColor,
            '--effect-color': defaultTheme.effectColor,
            '--rounding': defaultTheme.rounding,
            '--font-size': defaultTheme.fontSize,
            '--boundary-spacing': withCustomContent ? '0px' : defaultTheme.boundarySpacing,
            '--hov-background-color': hoverTheme.backgroundColor,
            '--hov-border-color': hoverTheme.borderColor,
            '--hov-border-width': hoverTheme.borderWidth,
            '--hov-text-color': hoverTheme.textColor,
            '--hov-highlight-color': hoverTheme.highlightColor,
            '--hov-effect-color': hoverTheme.effectColor,
            '--hov-rounding': hoverTheme.rounding,
            '--hov-font-size': hoverTheme.fontSize,
            '--hov-boundary-spacing': withCustomContent ? '0px' : hoverTheme.boundarySpacing,
            '--child-icon-color': defaultTheme.textColor,
            '--hov-child-icon-color': hoverTheme.textColor
          },
          ...props.style
        }}
        aria-label={Helpers.ifBlank(props.ariaLabel, props.text)}
        aria-disabled={isDisabled}
        type="button"
        disabled={isDisabled}
        {...{
          'data-state': state
        }}
        onClick={handleClick}
        onMouseOver={props.onMouseOver}
        onMouseOut={props.onMouseOut}
        onMouseEnter={props.onMouseEnter}
        onMouseLeave={props.onMouseLeave}
        onMouseDown={props.onMouseDown}
        onMouseUp={props.onMouseUp}
        onFocus={props.onFocus}
        onBlur={props.onBlur}
      >
        {content}
      </button>
    );
  }
);